import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { SaveBankruptcyConsequencesRequest } from "../../../../api/ClientFileApi";
import { useBankruptcyChecklistApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { ChecklistAnswer } from "../../../BankruptcyChecklist.types";
import { useBankruptcyChecklist } from "../../../hooks/useBankruptcyChecklist";
import { BankruptcyConsequencesAnswerKey, BankruptcyConsequencesFormValues } from "../BankruptcyConsequences.types";
import {
  EMPTY_BANKRUPTCY_CONSEQUENCES,
  applyAnswer,
  mapResponseToBankruptcyConsequences,
} from "../BankruptcyConsequences.utils";

function toRequest(values: BankruptcyConsequencesFormValues): SaveBankruptcyConsequencesRequest {
  return {
    hasOwnCheckingAccount: values.hasOwnCheckingAccount,
    isCheckingAccountOverdrawn: values.isCheckingAccountOverdrawn,
    hasRegisteredMobilePhone: values.hasRegisteredMobilePhone,
    isElectricityInOwnName: values.isElectricityInOwnName,
    isApartmentDistrictHeated: values.isApartmentDistrictHeated,
    isDistrictHeatingInOwnName: values.isDistrictHeatingInOwnName,
  };
}

export function useBankruptcyConsequences(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess } = useAlertContext();
  const queryClient = useQueryClient();
  const bankruptcyChecklistApi = useBankruptcyChecklistApi();

  const { data } = useBankruptcyChecklist(clientId);

  const saveMutation = useMutation({
    mutationFn: (request: SaveBankruptcyConsequencesRequest) =>
      bankruptcyChecklistApi.saveBankruptcyChecklistBankruptcyConsequences({
        clientId,
        saveBankruptcyConsequencesRequest: request,
      }),
    onSuccess: (saved) => {
      queryClient.setQueryData([ClientFileQueryKey.BankruptcyChecklist, clientId], saved);
      showSuccess(t("clients.client.debtSettlement.bankruptcyChecklist.saveSuccess"));
    },
  });

  const initialValues: BankruptcyConsequencesFormValues = useMemo(
    () => (data ? mapResponseToBankruptcyConsequences(data) : EMPTY_BANKRUPTCY_CONSEQUENCES),
    [data]
  );

  const formik = useFormikWithDirtyForm<BankruptcyConsequencesFormValues>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values) => {
      await saveMutation.mutateAsync(toRequest(values));
    },
  });

  const setAnswer = (key: BankruptcyConsequencesAnswerKey, value: ChecklistAnswer) => {
    void formik.setValues(applyAnswer(formik.values, key, value));
  };

  return { formik, setAnswer };
}
